import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Snackbar,
  Paper,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FilledInput,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { tokens } from "../../theme";
import DoctorService from "../../services/doctorService";
import PermissionGroupService from "../../services/doctorServices/PermissionGroupService";
import Headeer from "../../components/Headeer";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SecurityIcon from "@mui/icons-material/Security";

/**
 * 📧 InviteDoctor
 * Admin doktor tarafından alt doktor davet etme sayfası
 * Sadece email ile davet gönderir, kayıt link üzerinden tamamlanır
 */
export default function InviteDoctor() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('inviteDoctor');
  const doctorService = new DoctorService();
  const permissionService = new PermissionGroupService();

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [roleTitleError, setRoleTitleError] = useState("");
  const [groupsError, setGroupsError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  
  // Permission Groups state
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);

  // Fetch permission groups on mount
  useEffect(() => {
    fetchPermissionGroups();
  }, []);

  const fetchPermissionGroups = async () => {
    try {
      setGroupsLoading(true);
      const groups = await permissionService.getAllGroups();
      // Filter only doctor type groups
      let doctorGroups = groups.filter(group => group.type === 'doctor');
      
      // Get current doctor role
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const currentRole = user.role;
      
      // If current user is 'doctor' role (not admin), filter out FULL_DOCTOR default group
      if (currentRole === 'doctor') {
        doctorGroups = doctorGroups.filter(group => 
          !(group.name === 'FULL_DOCTOR' && group.is_default === true)
        );
      }
      
      setPermissionGroups(doctorGroups);
    } catch (error) {
      console.error("Error fetching permission groups:", error);
    } finally {
      setGroupsLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const capitalizeWords = (str) => {
    return str.trim().replace(/\b\w/g, char => char.toUpperCase());
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError("");
    setErrorMessage("");
  };

  const handleRoleTitleChange = (e) => {
    const value = e.target.value;
    setRoleTitle(value);
    setRoleTitleError("");
    setErrorMessage("");
  };

  const handleRoleTitleBlur = (e) => {
    const value = e.target.value;
    if (value.trim()) {
      const capitalizedValue = capitalizeWords(value);
      setRoleTitle(capitalizedValue);
    }
  };

  const handleGroupChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedGroups(
      typeof value === 'string' ? value.split(',') : value,
    );
    setGroupsError(""); // Clear error when groups are selected
    setErrorMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError("");
    setRoleTitleError("");
    setGroupsError("");
    setErrorMessage("");
    setSuccessMessage("");

    // Email validation
    if (!email.trim()) {
      setEmailError(t('emailRequired'));
      return;
    }

    if (!validateEmail(email)) {
      setEmailError(t('emailInvalid'));
      return;
    }

    // Role title validation
    if (!roleTitle.trim()) {
      setRoleTitleError(t('roleTitleRequired'));
      return;
    }

    // Permission groups validation
    if (!selectedGroups || selectedGroups.length === 0) {
      setGroupsError(t('groupsRequired'));
      return;
    }

    setLoading(true);

    try {
      // Capitalize role title before submitting
      const capitalizedRoleTitle = capitalizeWords(roleTitle);
      
      const response = await doctorService.inviteDoctor(
        email,
        capitalizedRoleTitle,
        selectedGroups.length > 0 ? selectedGroups : null
      );
      setSuccessMessage(t('invitationEmailSentSuccess', { email }));
      setOpenSnackbar(true);
      setEmail(""); // Clear form
      setRoleTitle(""); // Clear role title
      setSelectedGroups([]); // Clear selected groups
    } catch (error) {
      const errorMsg =
        error.response?.data?.error ||
        t('errorSendingInvitation');
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Box sx={{ marginRight: 2, marginLeft: 2 }}>
      <Headeer
        title={t('title')}
        subtitle={t('subtitle')}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          minHeight: "60vh",
          paddingTop: 4,
        }}
      >
        <Paper
          elevation={6}
          sx={{
            maxWidth: 700,
            width: "100%",
            padding: 4,
            backgroundColor: colors.primary[500],
            borderRadius: 3,
          }}
        >
          {/* Header Icon */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <PersonAddIcon
              sx={{
                fontSize: 60,
                color: colors.greenAccent[500],
                mb: 2,
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: colors.greenAccent[400],
                mb: 1,
              }}
            >
              {t('addNewDoctor')}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[300] }}>
              {t('sendEmailInvitation')}
            </Typography>
          </Box>

          {/* Info Box */}
          <Box
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              backgroundColor: colors.blueAccent[800],
              border: `2px solid ${colors.blueAccent[600]}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: colors.blueAccent[300], mb: 1, fontWeight: "bold" }}
            >
              📨 {t('invitationProcess')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.grey[100], lineHeight: 1.8 }}
            >
              1️⃣ {t('step1')}
              <br />
              2️⃣ {t('step2')}
              <br />
              3️⃣ {t('step3')}
              <br />
              4️⃣ {t('step4')}
              <br />
              5️⃣ {t('step5')}
            </Typography>
          </Box>

          {/* Error Alert */}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
          )}

          {/* Success Message */}
          {successMessage && !openSnackbar && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {successMessage}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Email Field */}
              <TextField
                fullWidth
                label={t('doctorEmailLabel')}
                name="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                error={Boolean(emailError)}
                helperText={emailError || t('registrationLinkHelper')}
                variant="filled"
                placeholder={t('emailPlaceholder')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: colors.greenAccent[500] }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiFilledInput-root": {
                    backgroundColor: colors.primary[600],
                  },
                }}
              />

              {/* Role Title Field */}
              <TextField
                fullWidth
                label={t('roleTitleLabel')}
                name="role_title"
                value={roleTitle}
                onChange={handleRoleTitleChange}
                onBlur={handleRoleTitleBlur}
                error={Boolean(roleTitleError)}
                helperText={
                  roleTitleError || t('roleTitleHelper')
                }
                variant="filled"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonAddIcon sx={{ color: colors.greenAccent[500] }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiFilledInput-root": {
                    backgroundColor: colors.primary[600],
                  },
                }}
              />

              {/* Permission Groups Selection */}
              <FormControl 
                fullWidth
                variant="filled"
                disabled={groupsLoading}
                error={Boolean(groupsError)}
              >
                <InputLabel id="permission-groups-label">
                  {t('permissionGroupsLabel')}
                </InputLabel>
                <Select
                  labelId="permission-groups-label"
                  id="permission-groups"
                  multiple
                  value={selectedGroups}
                  onChange={handleGroupChange}
                  input={<FilledInput />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const group = permissionGroups.find(g => g.id === value);
                        return (
                          <Chip 
                            key={value} 
                            label={group?.label || value}
                            size="small"
                            sx={{
                              backgroundColor: colors.greenAccent[700],
                              color: colors.grey[100],
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  startAdornment={
                    <InputAdornment position="start">
                      <SecurityIcon sx={{ color: colors.greenAccent[500], ml: 1 }} />
                    </InputAdornment>
                  }
                  sx={{
                    backgroundColor: colors.primary[600],
                    "& .MuiSelect-icon": {
                      color: colors.grey[100],
                    },
                  }}
                >
                  {groupsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      {t('loadingGroups')}
                    </MenuItem>
                  ) : permissionGroups.length === 0 ? (
                    <MenuItem disabled>
                      {t('noGroupsAvailable')}
                    </MenuItem>
                  ) : (
                    permissionGroups.map((group) => (
                      <MenuItem 
                        key={group.id} 
                        value={group.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: `${colors.greenAccent[700]}40`,
                          },
                          '&.Mui-selected': {
                            backgroundColor: `${colors.greenAccent[700]}60`,
                            '&:hover': {
                              backgroundColor: `${colors.greenAccent[700]}80`,
                            },
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                          <SecurityIcon sx={{ fontSize: 20, color: colors.greenAccent[400] }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {group.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                              {group.name}
                            </Typography>
                          </Box>
                          {group.is_default && (
                            <Chip 
                              label={t('default')} 
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                backgroundColor: `${colors.greenAccent[800]}40`,
                                color: colors.greenAccent[300],
                              }}
                            />
                          )}
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: groupsError ? colors.redAccent[500] : colors.grey[400],
                    mt: 0.5,
                    ml: 1.5,
                    display: 'block'
                  }}
                >
                  {groupsError || (selectedGroups.length > 0 
                    ? `${selectedGroups.length} group(s) selected.`
                    : "At least one permission group must be selected")
                  }
                </Typography>
              </FormControl>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontSize: "16px",
                  fontWeight: "bold",
                  background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.greenAccent[700]} 100%)`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[800]} 100%)`,
                  },
                  "&:disabled": {
                    background: colors.grey[700],
                  },
                }}
                endIcon={
                  loading ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    <SendIcon />
                  )
                }
              >
                {loading ? t('sending') : t('sendInvitationEmail')}
              </Button>
            </Box>
          </form>

          {/* Additional Info */}
          <Box
            sx={{
              mt: 4,
              p: 2.5,
              borderRadius: 2,
              backgroundColor: colors.greenAccent[800],
              border: `1px solid ${colors.greenAccent[600]}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: colors.greenAccent[100], fontWeight: "bold", mb: 1 }}
            >
              ✨ {t('advantages')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.grey[300], lineHeight: 1.6 }}
            >
              • <strong>{t('advantage1')}</strong> {t('advantage1Text')}
              <br />
              • <strong>{t('advantage2')}</strong> {t('advantage2Text')}
              <br />
              • <strong>{t('advantage3')}</strong> {t('advantage3Text')}
              <br />• <strong>{t('advantage4')}</strong> {t('advantage4Text')}
            </Typography>
          </Box>

          {/* Security Note */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              backgroundColor: colors.primary[600],
              border: `1px solid ${colors.grey[700]}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: colors.grey[400], display: "block", mb: 0.5 }}
            >
              🔐 {t('security')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.grey[300], fontSize: "12px" }}
            >
              {t('securityText')}
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "12px",
            backgroundColor: colors.greenAccent[500],
            boxShadow: `0 4px 16px rgba(76, 206, 172, 0.4)`,
            "& .MuiAlert-icon": {
              fontSize: "28px",
            },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "white" }}>
            ✅ {t('invitationSentSuccessfully')}
          </Typography>
          <Typography variant="body2" sx={{ color: "white", mt: 0.5 }}>
            {t('registrationLinkSent', { email })}
          </Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
}


